export interface ViewHealthBeaconPayload {
  scope_type: 'route' | 'overlay';
  scope_name: string;
  outcome: 'success' | 'timeout' | 'error';
  duration_ms: number;
  error_type?: string;
  markers_missing?: string[];
  /**
   * Which declared success variant settled the scope. Only present on
   * `outcome: 'success'` beacons for routes with variant-based success
   * (routes with multiple variants, or single-variant routes like 'LOADED').
   * Overlays have no variants and omit this field.
   *
   * The value must be in the backend allowlist (see
   * `apps/utils/views.py: ALLOWED_SUCCESS_VARIANTS`). Update in lockstep
   * with any new variant names introduced in `handle.health.success`.
   */
  success_variant?: string;
  /**
   * Optional CRM entity-type dimension. Backend allowlist coerces unknown
   * values to "custom" (see apps/utils/views.py:ALLOWED_ENTITY_TYPES).
   * Used by the slideout scope to slice adoption metrics per entity type.
   */
  entity_type?: string;
}

export type SuccessVariantRecord = Record<
  string,
  readonly [string, ...string[]]
>;

export interface RouteHealthConfig {
  /** Timeout in ms before the scope emits an `outcome: 'timeout'` beacon. */
  timeout: number;
  /**
   * Named success variants. A scope settles successfully when ALL markers in
   * any ONE variant are received. AT LEAST ONE variant must be declared —
   * routes with no success variants are not monitored.
   *
   * The variant name is not emitted to Grafana; it's a code-level label
   * for clarity (e.g. 'LOADED' vs 'EMPTY').
   *
   * NOTE: Route definitions should use `defineRouteHealth()` below to get
   * compile-time enforcement that at least one variant is declared; the
   * field type alone cannot enforce non-emptiness because `Record<string, V>`
   * accepts `{}`.
   */
  success: SuccessVariantRecord;
  /**
   * Optional failure markers. When any one of these fires, the scope
   * immediately emits an `outcome: 'error'` beacon with `error_type` set
   * to the marker name. No debounce.
   */
  failure?: readonly string[];
  /**
   * Optional override for the Grafana `scope_name` label.
   * Receives the resolved route params (NOT the URL pattern).
   * Use to include a URL param in the scope name — e.g.
   * `(params) => \`/settings/${params.section}\``. Default: the route
   * pattern as computed by `extractRoutePattern`.
   *
   * IMPORTANT: only use URL params that have a bounded, known set of
   * values. Including free-form ids (record ids, user ids) would blow
   * up cardinality.
   */
  scopeName?: (params: Readonly<Record<string, string>>) => string;
}

/**
 * Helper that enforces "at least one success variant" at compile time.
 *
 * `RouteHealthConfig.success` is typed as `Record<string, readonly [string, ...]>`
 * which accepts an empty object `{}`. Wrapping the config in this helper forces
 * TypeScript to infer the concrete success type from the literal, at which
 * point the `keyof T extends never ? never : T` branch rejects `{}`.
 */
export function defineRouteHealth<T extends SuccessVariantRecord>(
  config: Omit<RouteHealthConfig, 'success'> & {
    success: keyof T extends never ? never : T;
  }
): RouteHealthConfig {
  return config as RouteHealthConfig;
}

export interface ScopeState {
  id: string;
  scopeType: 'route' | 'overlay';
  scopeName: string; // captured at navigation start
  startTime: number; // performance.now()
  timeoutId: ReturnType<typeof setTimeout> | null;
  debounceId: ReturnType<typeof setTimeout> | null;
  /** variant name → set of required markers; empty object means no markers required (overlays) */
  successVariants: Record<string, Set<string>>;
  receivedMarkers: Set<string>;
  /** markers that immediately short-circuit scope to error outcome when fired */
  failureMarkers: Set<string>;
  settled: boolean;
  /** Caller-supplied entity type, included on every beacon for this scope. */
  entityType?: string;
  /** Caller-supplied success_variant override; only used when successVariants is empty. */
  successVariantOverride?: string;
}

export interface ViewHealthScopeOptions {
  /** Mirrors the legacy 3rd-arg-as-boolean signature. Defaults to true. */
  enabled?: boolean;
  /** CRM entity type for the rendered record (e.g. "ahoy_meeting"). */
  entityType?: string;
  /**
   * Caller-supplied success_variant override. For overlays with no
   * declared variant set, this value is what gets emitted on the
   * success beacon. Ignored for routes that already have variant
   * matching (variant matching wins).
   */
  successVariant?: string;
}

export interface ViewHealthContextValue {
  startScope(
    id: string,
    type: 'route' | 'overlay',
    name: string,
    options?: { entityType?: string; successVariant?: string }
  ): void;
  endScope(id: string): void;
  markReceived(scopeId: string, markerName: string): void;
  /** Update the caller-supplied options on an already-started scope. */
  updateScopeOptions(
    id: string,
    options: { entityType?: string; successVariant?: string }
  ): void;
  currentRoutePattern: string;
  /** True when the current route has an active health scope (handle.health is configured). */
  isRouteMonitored: boolean;
  /**
   * Monotonically increasing counter that bumps every time a route scope is
   * (re-)created. `useViewHealthMarker` depends on this so persistently-
   * mounted components (e.g. wildcard routes like `actions/*` where the
   * page stays mounted across `/actions/` → `/actions/1` navigations)
   * re-fire their markers for the new scope. Without this, the recreated
   * scope would sit empty until it times out at 15s.
   */
  routeScopeVersion: number;
}

export interface RouteHealthHandle {
  health?: RouteHealthConfig;
}

export interface UseViewHealthMarkerOptions {
  ready?: boolean;
  scopeId?: string;
}

export interface UseViewHealthErrorMarkerOptions {
  /**
   * When this transitions to true, fire the failure marker into the current
   * route scope. No-op once fired.
   */
  triggered: boolean;
  scopeId?: string;
}
